import React, { FC, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, SearchIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import ComboboxInput, { InputStatus } from '../../../components/combobox/combobox-input';
import {
  Combobox,
  ComboboxButton,
  ComboboxOption,
  ComboboxOptions,
} from '../../../components/combobox/index';
import { Flag, FlagCode } from '../../../components/flag/index';
import { Input } from '../../../components/input/index';
import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery, useLocale } from '../../../hooks/index';
import { ApiService } from '../../../services/index';
import { Country } from '../../../types/country.types';
import { onlyNumbers } from '../../../utils/string-transformers.util';

export type PhoneCountry = {
  iso2Code: string;
  name: string;
  prefix: string;
};

export type PhoneNumberItemsListProps = {
  data: Country[];
  isFetching: boolean;
  isLoading: boolean;
  selectedCountry?: PhoneCountry;
};

const PhoneNumberItemsList: FC<PhoneNumberItemsListProps> = ({
  data,
  isFetching,
  isLoading,
  selectedCountry,
}) => {
  if (!data || isFetching || isLoading) return <span>Loading ...</span>;

  return (
    <>
      {selectedCountry && (
        <ComboboxOption
          className="text-sm font-normal text-gray-700"
          value={{ iso2Code: '', prefix: '', name: '' } as unknown as string}
        >
          Reset
        </ComboboxOption>
      )}
      {data.map(({ name, callingCode, iso3, iso2 }) => (
        <ComboboxOption
          key={`${iso3}_${iso2}`}
          value={
            {
              iso2Code: iso2,
              prefix: callingCode,
              name,
            } as unknown as string
          }
        >
          <Flag countryCode={iso2} />
          <span className="text-sm text-gray-700">
            {name} {callingCode}
          </span>
        </ComboboxOption>
      ))}
    </>
  );
};

export type PhoneNumberComboboxProps = {
  disabled?: boolean;
  onPhoneChange?: (number: string) => void;
  onSelectCountry?: (country: PhoneCountry) => void;
  selectedPhoneNumber: {
    country: PhoneCountry | undefined;
    number: string;
  };
};

const PhoneNumberCombobox: FC<PhoneNumberComboboxProps> = ({
  onPhoneChange,
  onSelectCountry,
  disabled,
  selectedPhoneNumber,
}) => {
  const [phoneNumber, setPhoneNumber] = useState<{
    country: PhoneCountry;
    phone: string;
  }>({
    country: { iso2Code: '', name: '', prefix: '' },
    phone: '',
  });

  const [search, setSearch] = useState('');
  const [isFocused, setFocused] = useState(false);
  const onPhoneChangeRef = useRef(onPhoneChange);
  const onSelectCountryRef = useRef(onSelectCountry);
  const { locale } = useLocale();
  const {
    data: phoneCountriesData,
    isLoading,
    isFetching,
  } = useAppQuery<Country[]>({
    queryKey: [QueryKey.GET_COUNTRY, { search }],
    queryFn: () =>
      ApiService.get({ endpoint: BackendRoute.COUNTRIES }, { queryParams: { search }, locale }),
  });

  useEffect(() => {
    onPhoneChangeRef.current = onPhoneChange;
  }, [onPhoneChange]);

  useEffect(() => {
    onSelectCountryRef.current = onSelectCountry;
  }, [onSelectCountry]);

  useEffect(() => {
    if (!phoneCountriesData) return;
    if (!selectedPhoneNumber.number && !selectedPhoneNumber.country?.iso2Code) return;

    let selectedPhoneCountry = phoneCountriesData.find(
      ({ callingCode }) =>
        selectedPhoneNumber.number.startsWith("'" + callingCode) ||
        selectedPhoneNumber.number.startsWith(callingCode),
    );
    const selectedCountryByIso2 = phoneCountriesData.find(
      ({ iso2 }) => selectedPhoneNumber.country?.iso2Code === iso2,
    );

    selectedPhoneCountry = selectedCountryByIso2 || selectedPhoneCountry;

    if (!selectedPhoneCountry) return;

    const rawPhone = selectedPhoneNumber.number.replace(/\s+/g, '');
    const callingCode = selectedPhoneCountry.callingCode || '';
    const normalizedCode = callingCode.replace(/^\+/, '');
    const rawWithoutApostrophe = rawPhone.startsWith("'") ? rawPhone.slice(1) : rawPhone;
    const rawDigits = rawWithoutApostrophe.replace(/\D/g, '');
    const hasExplicitPrefix =
      normalizedCode &&
      rawWithoutApostrophe.startsWith('+') &&
      rawWithoutApostrophe.slice(1).startsWith(normalizedCode);
    const phoneWithoutPrefix = hasExplicitPrefix
      ? rawDigits.slice(normalizedCode.length)
      : rawDigits;

    setPhoneNumber((prev) => ({
      ...prev,
      phone: phoneWithoutPrefix,
      country: {
        iso2Code: selectedPhoneCountry.iso2 || '',
        name: selectedPhoneCountry.name || '',
        prefix: selectedPhoneCountry.callingCode || '',
      },
    }));

    onPhoneChangeRef.current?.(phoneWithoutPrefix);
    onSelectCountryRef.current?.({
      iso2Code: selectedPhoneCountry.iso2,
      name: selectedPhoneCountry.name,
      prefix: selectedPhoneCountry.callingCode,
    });
  }, [phoneCountriesData, selectedPhoneNumber.number, selectedPhoneNumber.country?.iso2Code]);

  const isSelectedCountry = phoneNumber.country.name;
  const ref = useRef<HTMLInputElement>(null);
  return (
    <Combobox
      as={'div'}
      className="relative"
      disabled={disabled}
      name={'phoneCountry'}
      onChange={(data: PhoneCountry) => {
        setSearch('');
        setPhoneNumber((prev) => ({ ...prev, country: data }));

        onSelectCountry?.(data);
      }}
      onClose={() => setSearch('')}
    >
      {() => (
        <>
          <div className="relative flex h-full w-full justify-between bg-gray-50">
            <div className="relative h-full w-[215px] shrink-0">
              <ComboboxInput
                customStatus={isFocused ? InputStatus.FOCUSED : InputStatus.IDLE_EMPTY}
                icon={
                  <ChevronDownIcon
                    className={twMerge(isSelectedCountry ? 'mt-3' : 'mt-0', disabled && 'hidden')}
                  />
                }
                iconBeforeInput={
                  <Flag className="mt-5" countryCode={phoneNumber?.country?.iso2Code as FlagCode} />
                }
                onBlur={() => setFocused(false)}
                onChange={() => ''}
                onFocus={() => setFocused(true)}
                placeholder={<AppFormattedMessage id="PHONE_NUMBER_OPTIONAL" />}
                readOnly
                value={
                  isSelectedCountry
                    ? `${phoneNumber.country?.name} ${phoneNumber?.country?.prefix}`
                    : ''
                }
                wrapperClassName={twMerge('w-full', disabled && 'border-none')}
              />
              <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
            </div>

            <Input
              customStatus={isFocused ? InputStatus.FOCUSED : InputStatus.IDLE_EMPTY}
              disabled={disabled}
              intelNumber={false}
              onBlur={() => setFocused(false)}
              onChange={(phone) => {
                if (isNaN(phone as unknown as number)) return;
                setPhoneNumber((prev) => ({ ...prev, phone }));
                onPhoneChange?.(phone);
              }}
              onFocus={() => setFocused(true)}
              regexp={(v) => onlyNumbers(v, 15)}
              shouldFormatNumber
              value={phoneNumber?.phone || ''}
              wrapperClassName="w-full"
            />
          </div>
          <ComboboxOptions className="w-full">
            <div className="relative flex items-center" onClick={() => ref.current?.focus()}>
              <SearchIcon className="absolute ml-[6px]" />
              <input
                className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
                onChange={({ target: { value } }) => setSearch(value)}
                ref={ref}
                value={search}
              />
            </div>

            <PhoneNumberItemsList
              data={phoneCountriesData || []}
              isFetching={isFetching}
              isLoading={isLoading}
              selectedCountry={phoneNumber.country}
            />
          </ComboboxOptions>
        </>
      )}
    </Combobox>
  );
};

export default PhoneNumberCombobox;
